import React from "react";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { fatigueService } from "../data/fatigueService";
import { useAsync } from "../data/useFatigueData";
import { RiskTierBadge } from "./RiskTierBadge";
import { RotaOverlayPanel } from "./RotaOverlayPanel";
import { Sparkline } from "./primitives";
import { useTheme } from "@mui/material";

interface CohortDetailDrawerProps {
  cohortId: string | null;
  onClose: () => void;
}

const CONTROLS: Record<string, string[]> = {
  red: [
    "Hold load flat — do not assign additional high-risk tasks.",
    "Prioritise rest: mandate recovery days and cap consecutive shifts.",
    "Trigger rota review and notify occupational health.",
  ],
  amber: [
    "Adjust shift sequencing where misalignment is highest.",
    "Remove discretionary overtime for the next two weeks.",
    "Push targeted recovery content; keep on watch.",
  ],
  green: ["Maintain current schedule.", "Continue routine monitoring."],
};

/** Detail drawer: contributing factors, rota overlay and suggested controls. */
export const CohortDetailDrawer: React.FC<CohortDetailDrawerProps> = ({
  cohortId,
  onClose,
}) => {
  const theme = useTheme();
  const { data: summary, loading } = useAsync(
    () => (cohortId ? fatigueService.getCohort(cohortId) : Promise.resolve(undefined)),
    [cohortId],
  );
  const { data: rota } = useAsync(
    () => (cohortId ? fatigueService.getRotaForCohort(cohortId) : Promise.resolve(undefined)),
    [cohortId],
  );

  return (
    <Drawer anchor="right" open={cohortId !== null} onClose={onClose}>
      <Box sx={{ width: { xs: 340, sm: 460 }, p: 3 }} role="dialog" aria-label="Cohort detail">
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={800}>
            {summary?.cohort.name ?? "Cohort"}
          </Typography>
          <IconButton onClick={onClose} aria-label="Close cohort detail">
            <CloseIcon />
          </IconButton>
        </Stack>

        {loading || !summary ? (
          <Skeleton variant="rounded" height={320} />
        ) : (
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <RiskTierBadge tier={summary.riskTier} />
              <Typography variant="caption" color="text.secondary">
                {summary.cohort.sector} · {summary.cohort.site} · {summary.cohort.headcount} people
              </Typography>
            </Stack>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <Metric label="FED / 1,000 workdays" value={`${summary.fedPer1000}`} />
              <Metric label="FED % of workdays" value={`${summary.fedPctWorkdays}%`} />
              <Metric label="Load–Readiness Misalignment" value={`${summary.lrmPct}%`} />
              <Metric label="Circadian Disruption Index" value={`${summary.cdiScore}/100`} />
              <Metric label="Physiological Deviation Cluster" value={`${summary.pdcPct}%`} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Recent FED trend
                </Typography>
                <Sparkline values={summary.sparkline} color={theme.palette.primary.main} width={120} height={28} />
              </Box>
            </Box>

            <Divider />
            {rota && <RotaOverlayPanel rota={rota} />}

            <Divider />
            <Box>
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
                Suggested operational controls
              </Typography>
              <Stack component="ul" spacing={1} sx={{ pl: 2, m: 0 }}>
                {CONTROLS[summary.riskTier].map((c) => (
                  <Typography key={c} component="li" variant="body2" color="text.secondary">
                    {c}
                  </Typography>
                ))}
              </Stack>
            </Box>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
};

const Metric: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Stack spacing={0.25}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6" fontWeight={800}>
      {value}
    </Typography>
  </Stack>
);
